import DataTable from '@/common/tableData/DataTable';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import useTournamentDetails from '@/hooks/useTournamentDetails';
import CopyField from '@/common/forms/CopyField';
import { toast } from 'react-toastify';
import { config, TournamentPayoutTransactionDto } from '@repo/common-types';
import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import { Button } from 'reactstrap';

const TournamentTransactionsTable = () => {
  const { tournamentId } = useParams();
  const { transactions: initialTransactions } = useTournamentDetails(tournamentId ?? '');
  const [transactions, setTransactions] = useState<TournamentPayoutTransactionDto[]>(initialTransactions ?? []);
  const [payPlayer, setPayPlayer] = useState<{ userId: string; amount: number }>({ userId: '', amount: 0 });

  useEffect(() => {
    setTransactions(initialTransactions ?? []);
  }, [initialTransactions]);

  const handlePayPlayer = async () => {
    if (!tournamentId || !payPlayer.userId || !payPlayer.amount) return;
    await api.post(`/v1/admin/tournaments/${tournamentId}/pay-player`, payPlayer);
    setPayPlayer({ userId: '', amount: 0 });
    const { data } = await api.get<TournamentPayoutTransactionDto[]>(
      `/v1/admin/tournaments/${tournamentId}/payout-transactions`,
    );
    setTransactions(data);
  };

  return (
    <DataTable
      title="Payouts"
      data={transactions ?? []}
      columns={[
        {
          header: 'Datetime',
          accessorKey: 'startTime',
          size: 125,
          cell: ({ row }) => {
            return (
              <div className="d-flex flex-column justify-content-start align-items-start">
                <h6>{format(new Date(row.original.createdAt), 'yyyy-MM-dd')}</h6>
                <p className="text-muted">{format(new Date(row.original.createdAt), 'HH:mm')}</p>
              </div>
            );
          },
        },
        {
          header: 'Player',
          accessorKey: 'user.alias',
          size: 100,
        },
        {
          header: 'Amount',
          accessorKey: 'amount',
          size: 70,
          cell: ({ row }) => <CurrencyDisplay amount={row.original.amount ?? 0} ticketType={row.original.ticketType} />,
        },
        {
          header: 'Status',
          accessorKey: 'status',
          size: 100,
          cell: (cell) => {
            const status = cell.getValue();
            return <div className="font-nunito text-[14px] font-extrabold leading-[20px]">{status}</div>;
          },
        },
        {
          header: 'Transaction',
          accessorKey: 'txId',
          size: 300,
          cell: ({ row }) => {
            const txId = row.original.txId ?? row.original.ticketOriginTxId;
            return (
              txId && (
                <CopyField
                  onCopied={() => toast.success('Transaction ID copied to clipboard')}
                  label={false}
                  name="Transaction ID"
                  value={txId}
                  link={`${config.blockchain.explorerUrl}/tx/${txId}`}
                />
              )
            );
          },
        },
      ]}
      headerExtra={
        <div className="d-flex align-items-center gap-2">
          <input
            placeholder="User ID"
            value={payPlayer.userId}
            onChange={(e) => setPayPlayer({ userId: e.target.value, amount: payPlayer.amount })}
            style={{ width: 220 }}
            className="form-control"
          />
          <input
            placeholder="Amount"
            type="number"
            step="0.01"
            value={payPlayer.amount}
            onChange={(e) => setPayPlayer({ userId: payPlayer.userId, amount: Number(e.target.value) })}
            style={{ width: 140 }}
            className="form-control"
          />
          <Button color="primary" disabled={!payPlayer.userId || !payPlayer.amount} onClick={handlePayPlayer}>
            Pay Player
          </Button>
        </div>
      }
    />
  );
};

export default TournamentTransactionsTable;
